"use client";

import { Suspense, useEffect, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { Loader2, AlertCircle } from "lucide-react";
import { authService } from "@/lib/services/auth.service";
import { examService } from "@/lib/services/exam.service";

function PasskeyAuth() {
  const router = useRouter();
  const searchParams = useSearchParams();

  const [status, setStatus] = useState<"loading" | "error">("loading");
  const [errorMessage, setErrorMessage] = useState("");

  const applicantId = searchParams.get("applicantId");
  const applicationNo = searchParams.get("applicationNo");
  const passkey = searchParams.get("passkey");

  useEffect(() => {
    if (!applicantId || !applicationNo || !passkey) return;

    const startExam = async () => {
      try {
        setStatus("loading");
        const result = await examService.startExamWithPasskey(
          applicantId,
          applicationNo,
          passkey
        );

        // Store authentication data for subsequent exam pages
        authService.loginWithPasskey(result.token, result.user);

        // Mock exams: go straight to attempt UI (skip ID / photo pre-check and instructions)
        const qs = new URLSearchParams();
        qs.set("sessionId", result.sessionId);
        if (result.setCode) qs.set("setCode", result.setCode);
        const suffix = qs.toString();
        router.replace(
          result.isMock
            ? `/exam/${result.examId}?${suffix}`
            : `/exam/${result.examId}/pre-check?${suffix}`
        );
      } catch (err: unknown) {
        setStatus("error");

        if (
          err &&
          typeof err === "object" &&
          "response" in err &&
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (err as any).response &&
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          typeof (err as any).response === "object" &&
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          "data" in (err as any).response &&
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (err as any).response.data &&
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          typeof (err as any).response.data === "object"
        ) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const responseData = (err as any).response.data as { error?: string; message?: string };
          setErrorMessage(
            responseData.message || responseData.error || "Failed to start the exam. Please try again."
          );
        } else if (err instanceof Error) {
          setErrorMessage(err.message);
        } else {
          setErrorMessage("Failed to start the exam. Please try again.");
        }
      }
    };

    void startExam();
  }, [applicantId, applicationNo, passkey, router]);

  if (status === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primaryGreen-50 via-primaryYellow-50 to-secondaryRed-50 px-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full mx-4 text-center">
          <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <AlertCircle className="h-8 w-8 text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Unable to Start Exam</h2>
          <p className="text-gray-600 mb-6">{errorMessage}</p>
          <p className="text-sm text-gray-400">Please contact your exam administrator if this issue persists.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primaryGreen-50 via-primaryYellow-50 to-secondaryRed-50 px-4">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full mx-4 text-center">
        <Loader2 className="h-12 w-12 text-primaryGreen-500 animate-spin mx-auto mb-4" />
        <h2 className="text-xl font-bold text-gray-900 mb-2">Starting Your Exam</h2>
        <p className="text-gray-600">Validating your credentials and preparing the exam...</p>
      </div>
    </div>
  );
}

function HomePageInner() {
  const searchParams = useSearchParams();
  const applicantId = searchParams.get("applicantId");
  const applicationNo = searchParams.get("applicationNo");
  const passkey = searchParams.get("passkey");

  if (applicantId && applicationNo && passkey) {
    return <PasskeyAuth />;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-10">
      <div className="w-full max-w-md space-y-4 text-center">
        <h1 className="text-2xl font-semibold text-gray-900">Passkey link required</h1>
        <p className="text-sm text-gray-600">
          Open this page using the exam invite link that contains <code>applicantId</code>,{" "}
          <code>applicationNo</code>, and <code>passkey</code>.
        </p>
      </div>
    </div>
  );
}

export default function HomePage() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-10">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      }
    >
      <HomePageInner />
    </Suspense>
  );
}

